import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Save, Plug, CheckCircle2, AlertCircle, ExternalLink, Sparkles, ArrowRight, Globe2, Telescope } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const FIRECRAWL_DOC_URL = "https://firecrawl.dev";
const TAVILY_DOC_URL = "https://tavily.com";

export default function IntegrationsSettings() {
  const { t } = useTranslation(["integrations", "nav", "common", "errors"]);
  const { tenant, setTenant } = useAuth();
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await api.get("/integrations/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoadingStatus(false);
    }
  }, [t]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Phase 8 — derive backward-compatible per-integration status from the
  // new ``status.crawl`` + ``status.search`` shape so the existing key cards
  // (Firecrawl + Tavily) keep working without changes.
  const firecrawlStatus = status
    ? { configured: !!status.crawl?.firecrawl_configured, source: tenant?.settings?.firecrawl_api_key_configured ? "tenant" : (status.crawl?.firecrawl_configured ? "platform" : "none") }
    : null;
  const tavilyStatus = status
    ? { configured: !!status.search?.configured, source: status.search?.source || "none" }
    : null;

  return (
    <div className="space-y-8" data-testid="integrations-settings-page">
      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t("nav:sections.settings")}</span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{t("integrations:title")}</h1>
        <p className="max-w-xl text-sm text-slate-500">{t("integrations:subtitle")}</p>
      </header>

      <LlmStatusCard
        tenant={tenant}
        status={status?.llm}
        loading={loadingStatus}
      />

      <CrawlerPreferenceCard
        tenant={tenant}
        status={status?.crawl}
        loading={loadingStatus}
        onSaved={(data) => { setTenant(data); refreshStatus(); }}
      />

      <IntegrationCard
        integration="firecrawl"
        docUrl={FIRECRAWL_DOC_URL}
        keyConfigured={tenant?.settings?.firecrawl_api_key_configured}
        status={firecrawlStatus}
        loading={loadingStatus}
        onSaved={(data) => { setTenant(data); refreshStatus(); }}
      />

      <IntegrationCard
        integration="tavily"
        docUrl={TAVILY_DOC_URL}
        keyConfigured={tenant?.settings?.tavily_api_key_configured}
        status={tavilyStatus}
        loading={loadingStatus}
        onSaved={(data) => { setTenant(data); refreshStatus(); }}
      />
    </div>
  );
}

// Phase 8 — Crawler preference card. Three radio options + an "Effective
// provider right now" footer line. Saves via PATCH /api/tenants/me.
function CrawlerPreferenceCard({ tenant, status, loading, onSaved }) {
  const { t } = useTranslation(["integrations", "common", "errors"]);
  const current = tenant?.settings?.crawler_preference || "auto";
  const [selected, setSelected] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(current); }, [current]);

  const effective = status?.effective_provider;
  const dirty = selected !== current;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tenants/me", { crawler_preference: selected });
      toast.success(t("integrations:crawler.saveSuccess"));
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSaving(false);
    }
  };

  const effectiveLabel =
    effective === "firecrawl"
      ? t("integrations:crawler.providers.firecrawl.label")
      : effective === "tavily_extract"
        ? t("integrations:crawler.providers.tavily_extract.label")
        : null;

  return (
    <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="integration-card-crawler">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
            <Telescope className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("integrations:crawler.title")}</h2>
            <p className="text-sm text-slate-500">{t("integrations:crawler.subtitle")}</p>
          </div>
        </div>
        {loading ? (
          <Badge variant="outline" className="border-slate-200 bg-white text-slate-500">
            <Loader2 className="mr-1 h-3 w-3 animate-spin" strokeWidth={2} />
          </Badge>
        ) : null}
      </div>

      <RadioGroup
        value={selected}
        onValueChange={setSelected}
        className="space-y-2"
        data-testid="crawler-preference-radio-group"
      >
        {["auto", "firecrawl", "tavily_extract"].map((opt) => (
          <label
            key={opt}
            data-testid={`crawler-preference-${opt}`}
            className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${selected === opt ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
          >
            <RadioGroupItem
              value={opt}
              id={`crawler-pref-${opt}`}
              className="mt-1"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-display text-sm font-semibold text-slate-900">
                  {t(`integrations:crawler.providers.${opt}.label`)}
                </span>
                {opt === "auto" && (
                  <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-700">
                    {t("integrations:crawler.providers.auto.recommended")}
                  </Badge>
                )}
                {opt === effective && (
                  <Badge data-testid="crawler-effective-here-badge" variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                    <CheckCircle2 className="mr-1 h-2.5 w-2.5" strokeWidth={2} />
                    {t("integrations:crawler.activeBadge")}
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">{t(`integrations:crawler.providers.${opt}.body`)}</p>
            </div>
          </label>
        ))}
      </RadioGroup>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="crawler-effective-line">
          <Globe2 className="h-3.5 w-3.5 text-slate-400" strokeWidth={1.75} />
          {effectiveLabel ? (
            <span>
              {t("integrations:crawler.effectiveNow")}: <span className="font-semibold text-slate-900">{effectiveLabel}</span>
            </span>
          ) : (
            <span data-testid="crawler-effective-none" className="text-amber-700">
              <AlertCircle className="mr-1 inline h-3.5 w-3.5" strokeWidth={2} />
              {t("integrations:crawler.effectiveNone")}
            </span>
          )}
        </p>
        <Button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="crawler-preference-save"
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} />{t("common:save")}</>}
        </Button>
      </div>
    </div>
  );
}

// ─── LLM status card (read-only — editing lives on /settings/llm) ───
function LlmStatusCard({ tenant, status, loading }) {
  const { t } = useTranslation(["integrations", "common"]);
  const source = status?.source || "none";
  const provider = tenant?.settings?.llm_provider || "emergent";
  const model = tenant?.settings?.llm_model || "gpt-5.4";

  const renderStatusBadge = () => {
    if (loading) {
      return (
        <Badge variant="outline" className="border-slate-200 bg-white text-slate-500">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" strokeWidth={2} />
        </Badge>
      );
    }
    if (source === "tenant") {
      return (
        <Badge data-testid="llm-status-badge" variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.tenant")}
        </Badge>
      );
    }
    if (source === "platform") {
      return (
        <Badge data-testid="llm-status-badge" variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
          <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.platform")}
        </Badge>
      );
    }
    return (
      <Badge data-testid="llm-status-badge" variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
        <AlertCircle className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.none")}
      </Badge>
    );
  };

  const hintKey = source === "tenant" ? "tenantHint" : source === "platform" ? "platformHint" : "noneHint";

  return (
    <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="integration-card-llm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
            <Sparkles className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t("integrations:llm.title")}</h2>
            <p className="text-sm text-slate-500">{t("integrations:llm.tagline")}</p>
          </div>
        </div>
        {renderStatusBadge()}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("integrations:llm.providerLabel")}</p>
          <p data-testid="llm-status-provider" className="mt-1 font-mono text-sm text-slate-900">{provider}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t("integrations:llm.modelLabel")}</p>
          <p data-testid="llm-status-model" className="mt-1 font-mono text-sm text-slate-900">{model}</p>
        </div>
      </div>

      <p className="text-xs text-slate-500">{t(`integrations:llm.${hintKey}`)}</p>

      <div className="flex items-center justify-end border-t border-slate-100 pt-4">
        <Button asChild data-testid="llm-configure-cta" className="bg-slate-900 text-white hover:bg-slate-800">
          <Link to="/settings/llm">
            {t("integrations:llm.configureCta")} <ArrowRight className="ml-1 h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function IntegrationCard({ integration, docUrl, keyConfigured, status, loading, onSaved }) {
  const { t } = useTranslation(["integrations", "common", "errors"]);
  const [apiKey, setApiKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const payloadField = `${integration}_api_key`;

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.patch("/tenants/me", { [payloadField]: apiKey });
      setApiKey("");
      toast.success(t("integrations:saveSuccess"));
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSubmitting(false);
    }
  };

  const clear = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch("/tenants/me", { [payloadField]: "" });
      setApiKey("");
      toast.success(t("integrations:cleared"));
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusBadge = () => {
    if (loading) {
      return (
        <Badge variant="outline" className="border-slate-200 bg-white text-slate-500">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" strokeWidth={2} />
        </Badge>
      );
    }
    const source = status?.source || "none";
    if (source === "tenant") {
      return (
        <Badge data-testid={`${integration}-status-badge`} variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.tenant")}
        </Badge>
      );
    }
    if (source === "platform") {
      return (
        <Badge data-testid={`${integration}-status-badge`} variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
          <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.platform")}
        </Badge>
      );
    }
    return (
      <Badge data-testid={`${integration}-status-badge`} variant="outline" className="border-amber-200 bg-amber-50 text-amber-800">
        <AlertCircle className="mr-1 h-3 w-3" strokeWidth={2} /> {t("integrations:status.none")}
      </Badge>
    );
  };

  return (
    <form onSubmit={save} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid={`integration-card-${integration}`} noValidate>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
            <Plug className="h-4 w-4" strokeWidth={1.75} />
          </div>
          <div className="space-y-1">
            <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{t(`integrations:${integration}.title`)}</h2>
            <p className="text-sm text-slate-500">{t(`integrations:${integration}.tagline`)}</p>
            <a href={docUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900">
              {t(`integrations:${integration}.help`)} <ExternalLink className="h-3 w-3" strokeWidth={2} />
            </a>
          </div>
        </div>
        {renderStatusBadge()}
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t(`integrations:${integration}.keyLabel`)}</Label>
        <Input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={keyConfigured ? "••••••••••••" : t(`integrations:${integration}.keyPlaceholder`)}
          data-testid={`${integration}-key-input`}
          className="h-11 font-mono text-sm"
          autoComplete="new-password"
        />
        <p className="text-xs text-slate-400">
          {keyConfigured ? t(`integrations:${integration}.configuredHint`) : t(`integrations:${integration}.defaultHint`)}
        </p>
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        {keyConfigured ? (
          <Button type="button" variant="outline" onClick={clear} disabled={submitting} data-testid={`${integration}-clear`} className="border-slate-200 text-slate-700 hover:bg-slate-50">
            {t("integrations:clear")}
          </Button>
        ) : <span />}
        <Button type="submit" disabled={submitting || !apiKey} data-testid={`${integration}-save`} className="bg-slate-900 text-white hover:bg-slate-800">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} />{t("common:save")}</>}
        </Button>
      </div>
    </form>
  );
}
